/**
 * BoqUploadDialog
 * ----------------
 * Optional AI-assisted Bill-of-Quantities upload tool for the quotation
 * create/edit modal. Designed as a NON-DESTRUCTIVE add-on:
 *   - Existing manual line entry continues to work untouched.
 *   - User can append, replace, or merge imported items into the line items.
 *   - Preview is fully editable before commit.
 *
 * Props:
 *   open: boolean
 *   onOpenChange: (next: boolean) => void
 *   existingLines: Array<{description, quantity, unit_price, tax_rate, discount?}>
 *   onCommit: (newLines: typeof existingLines) => void
 */
import { useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  RadioGroup,
  RadioGroupItem,
} from '../ui/radio-group';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  CloudArrowUp,
  FilePdf,
  FileXls,
  FileDoc,
  Image as ImageIcon,
  Trash,
  ArrowsClockwise,
  Sparkle,
  CircleNotch,
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCEPT_EXT = '.xlsx,.xls,.csv,.docx,.pdf,.jpg,.jpeg,.png,.webp';
const MAX_BYTES = 25 * 1024 * 1024;

const fileIcon = (name) => {
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (['xlsx', 'xls', 'csv'].includes(ext)) return <FileXls size={28} weight="duotone" className="text-emerald-500" />;
  if (ext === 'pdf') return <FilePdf size={28} weight="duotone" className="text-rose-500" />;
  if (ext === 'docx') return <FileDoc size={28} weight="duotone" className="text-sky-500" />;
  if (['jpg', 'jpeg', 'png', 'webp'].includes(ext)) return <ImageIcon size={28} weight="duotone" className="text-violet-500" />;
  return <CloudArrowUp size={28} weight="duotone" className="text-muted-foreground" />;
};

const fmtKb = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(2)} MB` : `${(b / 1024).toFixed(0)} KB`);

export default function BoqUploadDialog({ open, onOpenChange, existingLines, onCommit }) {
  const { token } = useAuth();
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [phase, setPhase] = useState('idle'); // idle | uploading | preview
  const [items, setItems] = useState([]); // editable preview rows
  const [meta, setMeta] = useState(null); // { source, notes, count }
  const [mergeMode, setMergeMode] = useState('append'); // append | replace | merge
  const inputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setItems([]);
    setMeta(null);
    setPhase('idle');
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleClose = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const validate = (f) => {
    if (!f) return 'Please pick a file.';
    if (f.size > MAX_BYTES) return 'File exceeds the 25 MB limit.';
    const ext = (f.name.split('.').pop() || '').toLowerCase();
    if (!ACCEPT_EXT.includes(ext)) return `Unsupported file type .${ext}`;
    return null;
  };

  const upload = async (f) => {
    const err = validate(f);
    if (err) { toast.error(err); return; }
    setFile(f);
    setPhase('uploading');
    try {
      const fd = new FormData();
      fd.append('file', f);
      // Send Authorization explicitly (defence in depth — axios.defaults are
      // also set globally, but FormData uploads sometimes drop them in older
      // browser/proxy combos).
      const res = await axios.post(`${API_URL}/quotations/extract-boq`, fd, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        timeout: 180000, // 3 min — AI vision on a multi-page PDF can take a while
      });
      const data = res.data || {};
      const list = (data.items || []).map((it) => ({
        description: it.description || '',
        specification: it.specification || '',
        unit: it.unit || '',
        quantity: Number(it.quantity) || 0,
        unit_price: Number(it.unit_price) || 0,
        tax_rate: Number(it.tax_rate) || 5,
        discount: 0,
      }));
      setItems(list);
      setMeta({ source: data.source, notes: data.notes, count: list.length });
      setPhase('preview');
      if (list.length === 0) {
        toast.warning('No BOQ items detected. Try a clearer file or paste manually.');
      } else {
        toast.success(`Extracted ${list.length} item${list.length === 1 ? '' : 's'} via ${data.source}`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || 'Upload failed';
      toast.error(detail);
      setPhase('idle');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  const updateRow = (idx, field, value) => {
    setItems((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: field === 'description' ? value : Number(value) || 0 };
      return next;
    });
  };

  const removeRow = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const commit = () => {
    if (items.length === 0) { toast.error('Nothing to commit.'); return; }
    let nextLines;
    if (mergeMode === 'replace') {
      nextLines = items.slice();
    } else if (mergeMode === 'merge') {
      const byKey = new Map();
      const norm = (s) => (s || '').trim().toLowerCase();
      [...existingLines, ...items].forEach((row) => {
        const key = norm(row.description);
        if (!key) return;
        if (byKey.has(key)) {
          const existing = byKey.get(key);
          existing.quantity = Number(existing.quantity || 0) + Number(row.quantity || 0);
          // Keep the higher unit_price (lets the user-entered manual price win
          // over the imported value if they were already nudging it).
          if ((row.unit_price || 0) > (existing.unit_price || 0)) {
            existing.unit_price = row.unit_price;
          }
        } else {
          byKey.set(key, { ...row });
        }
      });
      nextLines = Array.from(byKey.values());
    } else {
      // append (default): keep manual rows except the empty placeholder created on form open
      const keep = (existingLines || []).filter(
        (l) => (l.description || '').trim() !== '' || Number(l.unit_price) > 0
      );
      nextLines = [...keep, ...items];
    }
    onCommit(nextLines);
    toast.success(`${items.length} item${items.length === 1 ? '' : 's'} added to quotation`);
    handleClose(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="boq-upload-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkle size={20} weight="fill" className="text-amber-500" />
            Upload BOQ
          </DialogTitle>
          <DialogDescription>
            Excel, CSV, Word, PDF or scanned image. AI will extract the rows and let you
            edit them before adding to the quotation. Manual entry continues to work.
          </DialogDescription>
        </DialogHeader>

        {phase === 'idle' && (
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => inputRef.current?.click()}
            data-testid="boq-dropzone"
            className={`flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-14 cursor-pointer transition-colors ${
              dragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/30 hover:bg-muted/30'
            }`}
          >
            <CloudArrowUp size={48} weight="duotone" className="text-primary" />
            <div className="text-center">
              <div className="font-medium">Drag & drop your BOQ file here</div>
              <div className="text-xs text-muted-foreground mt-1">
                or click to browse • Excel / CSV / Word / PDF / Image • up to 25 MB
              </div>
            </div>
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT_EXT}
              className="hidden"
              data-testid="boq-file-input"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); }}
            />
          </div>
        )}

        {phase === 'uploading' && (
          <div className="flex flex-col items-center justify-center gap-3 py-14" data-testid="boq-uploading">
            <CircleNotch size={36} className="animate-spin text-primary" />
            <div className="font-medium">AI is reading {file?.name}…</div>
            <div className="text-xs text-muted-foreground">
              Excel/CSV is parsed locally; PDFs &amp; images go through Gemini 3 Pro vision.
            </div>
          </div>
        )}

        {phase === 'preview' && (
          <div className="space-y-4" data-testid="boq-preview">
            <div className="flex items-start gap-3 rounded-lg border bg-muted/30 p-3">
              {fileIcon(file?.name || '')}
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{file?.name}</div>
                <div className="text-xs text-muted-foreground">
                  {fmtKb(file?.size || 0)} • <Badge variant="outline" className="ml-1">{meta?.source}</Badge>
                </div>
                {meta?.notes && <div className="text-xs text-muted-foreground mt-1">{meta.notes}</div>}
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={reset} data-testid="boq-reimport-btn">
                <ArrowsClockwise size={14} className="mr-1" /> Re-import
              </Button>
            </div>

            <div>
              <Label className="text-sm">How should we add these items?</Label>
              <RadioGroup
                value={mergeMode}
                onValueChange={setMergeMode}
                className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2"
              >
                <Label
                  htmlFor="boq-mode-append"
                  className="flex items-start gap-2 rounded-md border p-3 cursor-pointer hover:bg-muted/50 has-[:checked]:border-primary has-[:checked]:bg-primary/5"
                >
                  <RadioGroupItem value="append" id="boq-mode-append" data-testid="boq-mode-append" />
                  <div>
                    <div className="text-sm font-medium">Append</div>
                    <div className="text-xs text-muted-foreground">Add below existing items</div>
                  </div>
                </Label>
                <Label
                  htmlFor="boq-mode-replace"
                  className="flex items-start gap-2 rounded-md border p-3 cursor-pointer hover:bg-muted/50 has-[:checked]:border-primary has-[:checked]:bg-primary/5"
                >
                  <RadioGroupItem value="replace" id="boq-mode-replace" data-testid="boq-mode-replace" />
                  <div>
                    <div className="text-sm font-medium">Replace</div>
                    <div className="text-xs text-muted-foreground">Clear and use only imported</div>
                  </div>
                </Label>
                <Label
                  htmlFor="boq-mode-merge"
                  className="flex items-start gap-2 rounded-md border p-3 cursor-pointer hover:bg-muted/50 has-[:checked]:border-primary has-[:checked]:bg-primary/5"
                >
                  <RadioGroupItem value="merge" id="boq-mode-merge" data-testid="boq-mode-merge" />
                  <div>
                    <div className="text-sm font-medium">Smart Merge</div>
                    <div className="text-xs text-muted-foreground">Combine duplicates by description</div>
                  </div>
                </Label>
              </RadioGroup>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm" data-testid="boq-preview-table">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Description</th>
                    <th className="text-right px-3 py-2 font-medium w-20">Qty</th>
                    <th className="text-right px-3 py-2 font-medium w-24">Unit Price</th>
                    <th className="text-right px-3 py-2 font-medium w-16">Tax %</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {items.length === 0 && (
                    <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">No rows extracted.</td></tr>
                  )}
                  {items.map((row, idx) => (
                    <tr key={idx} data-testid={`boq-preview-row-${idx}`}>
                      <td className="px-2 py-1.5">
                        <Input
                          value={row.description}
                          onChange={(e) => updateRow(idx, 'description', e.target.value)}
                          className="h-8 text-sm"
                          data-testid={`boq-preview-desc-${idx}`}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <Input
                          type="number" min="0" step="0.01"
                          value={row.quantity}
                          onChange={(e) => updateRow(idx, 'quantity', e.target.value)}
                          className="h-8 text-right"
                          data-testid={`boq-preview-qty-${idx}`}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <Input
                          type="number" min="0" step="0.01"
                          value={row.unit_price}
                          onChange={(e) => updateRow(idx, 'unit_price', e.target.value)}
                          className="h-8 text-right"
                          data-testid={`boq-preview-price-${idx}`}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <Input
                          type="number" min="0" max="100"
                          value={row.tax_rate}
                          onChange={(e) => updateRow(idx, 'tax_rate', e.target.value)}
                          className="h-8 text-right"
                          data-testid={`boq-preview-tax-${idx}`}
                        />
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8"
                          onClick={() => removeRow(idx)} data-testid={`boq-preview-remove-${idx}`}>
                          <Trash size={14} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleClose(false)} data-testid="boq-cancel-btn">
            Cancel
          </Button>
          {phase === 'preview' && (
            <Button type="button" onClick={commit} data-testid="boq-commit-btn" disabled={items.length === 0}>
              Add {items.length} item{items.length === 1 ? '' : 's'} to quotation
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
